import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import ContractIqTable, {
  FieldVerificationData, SortColumn, SortDirection,
} from './components/ContractIqTable/ContractIqTable';
import ContractIqRowSkeleton from './components/ContractIqTable/ContractIqRowSkeleton';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';
import Pagination from '@/components/common/Pagination/Pagination';
import { useGetPaginatedFieldVerifications } from './hooks/useGetPaginatedFieldVerifications';
import { useRemoveFieldVerification } from './hooks/useRemoveFieldVerification';
import { useGetReasonCodes } from '@/pages/SystemAdmin/ReasonCodes/hooks/useGetReasonCodes';
import { EntityTypeEnum, OrderType } from '@/graphql/contracts/_generated_/contracts';
import { useSearchUsers } from '@/hooks/identity/useSearchUsers';
import { User } from '@/hooks/identity/responseModels/user';
import { CONTRACT_IQ_TYPE } from '@/config/reasonTypes.config';
import { useAddQueryToUrl } from '@/components/common/TagTabs/useAddQueryToUrl';
import { useGetCurrentUserId } from '@/hooks/identity/useGetCurrentUserId';
import { fieldVerificationRefreshTrigger } from './ContractIq.state';

import styles from './contract-iq.module.scss';

const CONTRACT_IQ_TAB_NAME = 'Contract-IQ';
const SYSTEM_USER_ID = '00000000-0000-0000-0000-000000000000';

export default function ContractIqContainer() {
  const { contractId }: { contractId: UUID } = useParams();
  const { getValue } = useAddQueryToUrl();
  const isTabActive = getValue('tab') === CONTRACT_IQ_TAB_NAME;
  const [hasBeenActivated, setHasBeenActivated] = useState(false);
  const refreshTrigger = useRecoilValue(fieldVerificationRefreshTrigger);
  const setRefreshTrigger = useSetRecoilState(fieldVerificationRefreshTrigger);
  const prevContractIdRef = useRef<string>(contractId);
  const { getCurrentUserId, currentUserId } = useGetCurrentUserId();

  const {
    getFieldVerifications,
    verificationsData,
    isLoading,
  } = useGetPaginatedFieldVerifications();
  const { getReasonCodes, reasonCodes } = useGetReasonCodes();
  const { searchUsers } = useSearchUsers();

  const [users, setUsers] = useState<User[]>([]);
  const fetchedUserIdsRef = useRef<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [order, setOrder] = useState(OrderType.Descending);
  const [sortColumn, setSortColumn] = useState<SortColumn>('verifiedAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [columnWidths, setColumnWidths] = useState<number[] | null>(null);
  const [selectedFieldFilters, setSelectedFieldFilters] = useState<string[]>([]);
  const [removeConfirmVerification, setRemoveConfirmVerification] = useState<FieldVerificationData | null>(null);
  const { removeFieldVerification, isLoading: isRemoving } = useRemoveFieldVerification();

  const verifications = useMemo(() => verificationsData?.data ?? [], [verificationsData?.data]);
  const totalPages = verificationsData?.totalPages ?? 0;
  const isVisibleEmptyMessage = !isLoading && verifications.length === 0 && hasBeenActivated;
  const isVisiblePagination = !isLoading && verifications.length > 0 && totalPages > 1;

  const onColumnResize = useCallback((widths: number[]) => {
    setColumnWidths(widths);
  }, []);

  // Collect all unique field values across all current verifications
  const allUniqueFields = useMemo(() => {
    const fieldSet = new Set<string>();
    verifications.forEach((v) => {
      v.fields.forEach((f) => {
        if (f) fieldSet.add(f);
      });
    });
    return Array.from(fieldSet).sort();
  }, [verifications]);

  const reasonCodeNames = useMemo(() => {
    const map: Record<string, string> = {};
    reasonCodes.forEach((rc) => {
      map[rc.code] = rc.name;
    });
    return map;
  }, [reasonCodes]);

  // Client-side sorting for non-date columns (date is sorted server-side via API)
  const sortedVerifications = useMemo(() => {
    if (sortColumn === 'verifiedAt') return verifications;

    return [...verifications].sort((a, b) => {
      let valA: string;
      let valB: string;

      switch (sortColumn) {
        case 'type':
          valA = (a.type ?? '').toLowerCase();
          valB = (b.type ?? '').toLowerCase();
          break;
        case 'fields':
          valA = a.fields.filter(Boolean).join(', ').toLowerCase();
          valB = b.fields.filter(Boolean).join(', ').toLowerCase();
          break;
        case 'reasonCode':
          valA = (reasonCodeNames[a.reasonCode] ?? a.reasonCode).toLowerCase();
          valB = (reasonCodeNames[b.reasonCode] ?? b.reasonCode).toLowerCase();
          break;
        case 'updated':
          valA = a.updated ?? '';
          valB = b.updated ?? '';
          break;
        case 'user': {
          const userA = users.find((u) => u.Id === a.userID);
          const userB = users.find((u) => u.Id === b.userID);
          valA = a.userID === SYSTEM_USER_ID
            ? 'system'
            : userA ? `${userA.FirstName ?? ''} ${userA.LastName ?? ''}`.trim().toLowerCase() : '';
          valB = b.userID === SYSTEM_USER_ID
            ? 'system'
            : userB ? `${userB.FirstName ?? ''} ${userB.LastName ?? ''}`.trim().toLowerCase() : '';
          break;
        }
        case 'comment':
          valA = (a.comment ?? '').toLowerCase();
          valB = (b.comment ?? '').toLowerCase();
          break;
        default:
          return 0;
      }

      return sortDirection === 'asc'
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    });
  }, [verifications, sortColumn, sortDirection, reasonCodeNames, users]);

  // Reset state when contract changes
  useEffect(() => {
    if (prevContractIdRef.current !== contractId) {
      prevContractIdRef.current = contractId;
      setCurrentPage(1);
      setOrder(OrderType.Descending);
      setSortColumn('verifiedAt');
      setSortDirection('desc');
      setColumnWidths(null);
      setSelectedFieldFilters([]);
      setUsers([]);
      fetchedUserIdsRef.current = new Set();
    }
  }, [contractId]);

  // Track when tab has been activated
  useEffect(() => {
    if (isTabActive && !hasBeenActivated) {
      setHasBeenActivated(true);
    }
  }, [isTabActive, hasBeenActivated]);

  // Fetch current user ID on mount
  useEffect(() => {
    getCurrentUserId();
  }, [getCurrentUserId]);

  // Fetch reason codes when tab is first activated
  useEffect(() => {
    if (hasBeenActivated) {
      getReasonCodes(CONTRACT_IQ_TYPE);
    }
  }, [hasBeenActivated, getReasonCodes]);

  // Fetch verifications when tab activates, contract changes, page/order changes, or refresh is triggered
  useEffect(() => {
    if (hasBeenActivated) {
      getFieldVerifications({
        entityID: contractId,
        entityType: EntityTypeEnum.CONTRACT,
        pageNumber: currentPage,
        order,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBeenActivated, contractId, currentPage, order, refreshTrigger]);

  // Resolve user details when new verification data arrives
  useEffect(() => {
    if (!verificationsData?.data || verificationsData.data.length === 0) return;

    const newUserIds = verificationsData.data
      .map((v) => v.userID)
      .filter((id) => id !== SYSTEM_USER_ID && !fetchedUserIdsRef.current.has(id));
    const uniqueNewUserIds = [...new Set(newUserIds)];

    if (uniqueNewUserIds.length > 0) {
      uniqueNewUserIds.forEach((id) => fetchedUserIdsRef.current.add(id));
      searchUsers({ ids: uniqueNewUserIds }).then((searchResult) => {
        setUsers((prev) => {
          const existingUserIds = new Set(prev.map((u) => u.Id));
          const newUsers = searchResult.filter((u) => !existingUserIds.has(u.Id));
          return [...prev, ...newUsers];
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationsData]);

  const onColumnSort = (column: SortColumn) => {
    const newDirection: SortDirection =
      sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc';

    setSortColumn(column);
    setSortDirection(newDirection);

    // For date column, also update API order and reset pagination
    if (column === 'verifiedAt') {
      const newOrder = newDirection === 'asc' ? OrderType.Ascending : OrderType.Descending;
      setOrder(newOrder);
      setCurrentPage(1);
    }
  };

  const onPageChange = (page: number) => {
    setCurrentPage(page);
  };

  const loadingSkeleton = Array.from({ length: 3 }).map((_, index) => (
    <ContractIqRowSkeleton key={index} className="mb-10" />
  ));

  return (
    <div
      className={`${styles['contract-iq-container']} ${
        isVisiblePagination ? styles['contract-iq-container-with-pagination'] : ''
      }`}
    >
      {isLoading && loadingSkeleton}

      {!isLoading && verifications.length > 0 && (
        <ContractIqTable
          verifications={sortedVerifications}
          users={users}
          reasonCodeNames={reasonCodeNames}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onColumnSort={onColumnSort}
          columnWidths={columnWidths}
          onColumnResize={onColumnResize}
          allUniqueFields={allUniqueFields}
          selectedFieldFilters={selectedFieldFilters}
          onFieldFilterChange={setSelectedFieldFilters}
          currentUserId={currentUserId}
          onRemoveVerification={(v) => setRemoveConfirmVerification(v)}
        />
      )}

      {isVisibleEmptyMessage && (
        <div className={styles['empty-message']}>
          There are no field verifications yet for this contract
        </div>
      )}

      {isVisiblePagination && (
        <div className={styles['contract-iq-footer']}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onClickPage={onPageChange}
            disabled={isLoading}
          />
        </div>
      )}
      {removeConfirmVerification && (
        <AgConfirmationModal
          isOpen={true}
          onClose={() => setRemoveConfirmVerification(null)}
          onConfirm={() => {
            removeFieldVerification(
              { fieldVerificationID: removeConfirmVerification.fieldVerificationID },
              () => {
                setRemoveConfirmVerification(null);
                setRefreshTrigger((prev) => prev + 1);
              }
            );
          }}
          title="Remove Verification"
          confirmText={isRemoving ? 'Removing...' : 'Yes, Mark as Removed'}
          closeText="Cancel"
          isConfirmButtonDisabled={isRemoving}
          closeOnConfirm={false}
          contentStyle={{ width: 420 }}
        >
          <span>Are you sure you want to remove this verification?</span>
          <br />
          <span>
            The entry will be marked as <strong>Removed</strong> and will remain visible
            in the table, but it will no longer count as a valid verification.
          </span>
        </AgConfirmationModal>
      )}
    </div>
  );
}
